import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

export interface ApiErrorBody {
  error: { code: string; message: string; details?: Record<string, unknown>; requestId?: string };
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

const REFRESH_KEY = 'cl_admin_refresh';

/**
 * Access token lives in memory only; the refresh token in sessionStorage (cleared when the
 * tab closes). On 401 TOKEN_EXPIRED the client refreshes once (single-flight) and retries.
 */
let accessToken: string | null = null;
let refreshInFlight: Promise<string | null> | null = null;
let onLoggedOut: () => void = () => undefined;

export const tokenStore = {
  set(access: string, refresh: string) {
    accessToken = access;
    sessionStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    accessToken = null;
    sessionStorage.removeItem(REFRESH_KEY);
  },
  hasRefresh: () => !!sessionStorage.getItem(REFRESH_KEY),
  onLoggedOut(cb: () => void) {
    onLoggedOut = cb;
  },
};

export const api = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1`,
  timeout: 20_000,
});

api.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

export async function refreshTokens(): Promise<string | null> {
  const refreshToken = sessionStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return null;
  refreshInFlight ??= axios
    .post(`${api.defaults.baseURL}/auth/refresh`, { refreshToken })
    .then((r) => {
      tokenStore.set(r.data.tokens.accessToken, r.data.tokens.refreshToken);
      return r.data.tokens.accessToken as string;
    })
    .catch(() => {
      tokenStore.clear();
      return null;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

api.interceptors.response.use(undefined, async (error: AxiosError<ApiErrorBody>) => {
  // File downloads (`responseType: 'blob'`) get their JSON error body as a Blob; parse it so the
  // token refresh below and errorMessage() still see the error code.
  const body: unknown = error.response?.data;
  if (error.response && body instanceof Blob && body.type.includes('json')) {
    try {
      error.response.data = JSON.parse(await body.text());
    } catch {
      // not JSON after all; leave as is
    }
  }
  const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
  const code = error.response?.data?.error?.code;
  if (error.response?.status === 401 && original && !original._retried && code === 'TOKEN_EXPIRED') {
    original._retried = true;
    const token = await refreshTokens();
    if (token) return api(original);
  }
  if (error.response?.status === 401) {
    tokenStore.clear();
    onLoggedOut();
  }
  return Promise.reject(error);
});

export function errorMessage(e: unknown): string {
  const err = e as AxiosError<ApiErrorBody>;
  return err.response?.data?.error?.message ?? err.message ?? 'Something went wrong';
}

/** GETs [path] as a file and hands it to the browser as a download named [filename]. */
export async function downloadFile(path: string, params: Record<string, unknown>, filename: string) {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ''));
  // Large exports stream for a while: no client-side timeout.
  const r = await api.get<Blob>(path, { params: clean, responseType: 'blob', timeout: 0 });
  const url = URL.createObjectURL(r.data);
  const a = Object.assign(document.createElement('a'), { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export const newIdempotencyKey = () => crypto.randomUUID();
